import { getPrimaryImageFileId } from 'src/modules/shared/utils/catalog/property-media.util';
import React, { useMemo } from 'react';
import { enUS, es } from 'date-fns/locale';
import { PiBuildingApartment } from 'react-icons/pi';
import { EmptyState } from 'src/modules/shared/components/atoms/states/empty-state';
import { useFilesMetadata } from 'src/modules/shared/hooks/api/files';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import type { PropertiesListProps } from '../../types/properties-list.types';
import { buildPropertyCardViewModel } from '../../utils/properties-list.util';
import { PropertyAssigneeTagDropdown } from '../molecules/property-assignee-tag-dropdown';
import { PropertyListCard } from './property-list-card';
import { PropertyListLoadingCard } from './property-list-loading-card';

export const PropertiesList: React.FC<PropertiesListProps> = ({
  properties,
  loading,
  isLoadingMore = false,
  users,
  usersLoading,
  isAssigning = false,
  onUserAssign,
  onEdit,
  onDeactivate,
}) => {
  const { language, t } = useI18n();
  const distanceLocale = language === 'en-US' ? enUS : es;

  const usersById = useMemo(
    () => new Map(users.map((user) => [user.id, user])),
    [users]
  );
  const primaryImageFileIds = useMemo(
    () =>
      properties
        .map((property) => getPrimaryImageFileId(property))
        .filter((fileId): fileId is string => fileId != null),
    [properties]
  );
  const { files, loading: loadingFiles } =
    useFilesMetadata(primaryImageFileIds);
  const fileUrlById = useMemo(
    () =>
      new Map(
        files
          .filter(
            (file): file is typeof file & { id: string; url: string } =>
              typeof file.id === 'string' &&
              file.id.length > 0 &&
              typeof file.url === 'string' &&
              file.url.length > 0
          )
          .map((file) => [file.id, file.url])
      ),
    [files]
  );

  if (loading && properties.length === 0) {
    return (
      <div className="flex flex-col gap-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <PropertyListLoadingCard key={index} />
        ))}
      </div>
    );
  }

  if (!loading && properties.length === 0) {
    return (
      <EmptyState
        title={t('catalog.properties.table.emptyState.title')}
        description={t('catalog.properties.table.emptyState.description')}
        icon={
          <PiBuildingApartment className="h-full w-full text-primary-500" />
        }
        className="min-h-[320px]"
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {properties.map((property) => {
        const card = buildPropertyCardViewModel(
          property,
          fileUrlById,
          distanceLocale,
          onDeactivate,
          t
        );

        return (
          <PropertyListCard
            key={property.id}
            card={card}
            loadingFiles={loadingFiles}
            renderAssignee={(currentProperty) => (
              <PropertyAssigneeTagDropdown
                property={currentProperty}
                users={users}
                usersById={usersById}
                usersLoading={usersLoading}
                isAssigning={isAssigning}
                onUserAssign={onUserAssign}
                t={t}
              />
            )}
            onEdit={onEdit}
            onDeactivate={onDeactivate}
            t={t}
          />
        );
      })}

      {isLoadingMore ? <PropertyListLoadingCard /> : null}
    </div>
  );
};
